/*
 * =========================================================
 * CHEMARRY COLLECTION GRID v2.0
 * Catálogo global para Gatitos y Perritos
 * Mobile first · Shopify Dawn 15.4.1
 * =========================================================
 */

(() => {
  'use strict';

  const SELECTORS = {
    component: 'chemarry-collection-grid',

    toggle: '[data-grid-toggle]',
    toggleText: '[data-grid-toggle-text]',
    catalog: '[data-grid-catalog]',
    render: '[data-grid-render]',

    products: '[data-grid-products]',
    productItem: '[data-grid-product-item]',
    count: '[data-grid-count]',
    introCount: '[data-grid-intro-count]',

    filterOpen: '[data-grid-filter-open]',
    filterClose: '[data-grid-filter-close]',
    filterCount: '[data-grid-filter-count]',
    drawer: '[data-grid-drawer]',
    drawerOverlay: '[data-grid-drawer-overlay]',
    facetsForm: '[data-grid-facets-form]',
    filterInput: '[data-grid-filter-input]',
    priceInput: '[data-grid-price-input]',
    applyFilters: '[data-grid-apply-filters]',
    clearFilters: '[data-grid-clear-filters]',
    facetRemove: '[data-grid-facet-remove]',

    sortOpen: '[data-grid-sort-open]',
    sortClose: '[data-grid-sort-close]',
    sortSheet: '[data-grid-sort-sheet]',
    sortOverlay: '[data-grid-sort-overlay]',
    sortOption: '[data-grid-sort-option]',
    sortSelect: '[data-grid-sort-select]',

    paginationLink: '[data-grid-pagination-link]',

    variantSelect: '[data-grid-variant-select]',
    productImage: '[data-grid-product-image]',
    price: '[data-grid-price]',
    comparePrice: '[data-grid-compare-price]',
    availability: '[data-grid-availability]',
    addButton: '[data-grid-add]',

    toast: '[data-grid-toast]'
  };

  const CLASSES = {
    open: 'is-open',
    loading: 'is-loading',
    visible: 'is-visible',
    error: 'is-error',
    soldOut: 'is-sold-out',
    active: 'is-active',
    bodyLock: 'chemarry-grid-lock'
  };

  class ChemarryCollectionGrid extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.toastTimer = null;
      this.filterTimer = null;
      this.priceTimer = null;
      this.lastFocusedElement = null;
      this.isInitialized = false;
      this.isRequesting = false;

      this.handleToggle = this.handleToggle.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;

      this.cacheElements();
      this.bindEvents();
      this.setInitialState();
      this.initializeVariants();
      this.updateFilterLabels();
    }

    disconnectedCallback() {
      this.unbindEvents();
      this.cancelCurrentRequest();

      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.filterTimer);
      window.clearTimeout(this.priceTimer);

      this.unlockBody();
      this.isInitialized = false;
    }

    cacheElements() {
      this.toggleButton = this.querySelector(SELECTORS.toggle);
      this.toggleText = this.querySelector(SELECTORS.toggleText);
      this.catalog = this.querySelector(SELECTORS.catalog);
      this.renderContainer = this.querySelector(SELECTORS.render);
      this.toast = this.querySelector(SELECTORS.toast);
    }

    bindEvents() {
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', this.handleToggle);
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('submit', this.handleSubmit);

      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('popstate', this.handlePopState);
    }

    unbindEvents() {
      if (this.toggleButton) {
        this.toggleButton.removeEventListener(
          'click',
          this.handleToggle
        );
      }

      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('submit', this.handleSubmit);

      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('popstate', this.handlePopState);
    }

    setInitialState() {
      const desktopOpen =
        this.dataset.openDesktop === 'true';

      const mobileOpen =
        this.dataset.openMobile === 'true';

      const isDesktop = window.matchMedia(
        '(min-width: 990px)'
      ).matches;

      const shouldOpen = isDesktop ? desktopOpen : mobileOpen;

      this.setCatalogState(shouldOpen, false);
    }

    handleToggle() {
      this.setCatalogState(
        !this.classList.contains(CLASSES.open),
        true
      );
    }

    setCatalogState(isOpen, focusCatalog = false) {
      this.classList.toggle(CLASSES.open, isOpen);

      if (this.toggleButton) {
        this.toggleButton.setAttribute(
          'aria-expanded',
          String(isOpen)
        );
      }

      if (this.catalog) {
        this.catalog.setAttribute(
          'aria-hidden',
          String(!isOpen)
        );
      }

      this.updateToggleText(isOpen);

      if (isOpen && focusCatalog) {
        window.requestAnimationFrame(() => {
          this.scrollCatalogIntoView();
        });
      }
    }

    updateToggleText(isOpen) {
      if (!this.toggleButton || !this.toggleText) return;

      const openText =
        this.toggleButton.dataset.openText ||
        'Ver catálogo completo';

      const closeText =
        this.toggleButton.dataset.closeText ||
        'Ocultar catálogo';

      this.toggleText.textContent = isOpen
        ? closeText
        : openText;
    }

    scrollCatalogIntoView() {
      if (!this.catalog) return;

      const catalogTop =
        this.catalog.getBoundingClientRect().top +
        window.scrollY;

      const offset = this.getStickyHeaderOffset() + 16;

      window.scrollTo({
        top: Math.max(0, catalogTop - offset),
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    }

    getStickyHeaderOffset() {
      const stickyHeader =
        document.querySelector(
          '.section-header.shopify-section-header-sticky'
        ) ||
        document.querySelector(
          '.shopify-section-header-sticky'
        ) ||
        document.querySelector('sticky-header');

      if (!stickyHeader) return 0;

      return stickyHeader.getBoundingClientRect().height || 0;
    }

    handleClick(event) {
      const filterOpen = event.target.closest(
        SELECTORS.filterOpen
      );

      if (filterOpen && this.contains(filterOpen)) {
        event.preventDefault();
        this.openDrawer(filterOpen);
        return;
      }

      const filterClose = event.target.closest(
        SELECTORS.filterClose
      );

      if (filterClose && this.contains(filterClose)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const drawerOverlay = event.target.closest(
        SELECTORS.drawerOverlay
      );

      if (drawerOverlay && this.contains(drawerOverlay)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const sortOpen = event.target.closest(
        SELECTORS.sortOpen
      );

      if (sortOpen && this.contains(sortOpen)) {
        event.preventDefault();
        this.openSortSheet(sortOpen);
        return;
      }

      const sortClose = event.target.closest(
        SELECTORS.sortClose
      );

      if (sortClose && this.contains(sortClose)) {
        event.preventDefault();
        this.closeSortSheet();
        return;
      }

      const sortOverlay = event.target.closest(
        SELECTORS.sortOverlay
      );

      if (sortOverlay && this.contains(sortOverlay)) {
        event.preventDefault();
        this.closeSortSheet();
        return;
      }

      const sortOption = event.target.closest(
        SELECTORS.sortOption
      );

      if (sortOption && this.contains(sortOption)) {
        event.preventDefault();
        this.applySort(sortOption);
        return;
      }

      const clearFilters = event.target.closest(
        SELECTORS.clearFilters
      );

      if (clearFilters && this.contains(clearFilters)) {
        event.preventDefault();
        this.closeAllPanels();

        this.loadUrl(clearFilters.href, {
          updateHistory: true,
          scrollToCatalog: false
        });

        return;
      }

      const facetRemove = event.target.closest(
        SELECTORS.facetRemove
      );

      if (facetRemove && this.contains(facetRemove)) {
        event.preventDefault();

        this.loadUrl(facetRemove.href, {
          updateHistory: true,
          scrollToCatalog: false
        });

        return;
      }

      const paginationLink = event.target.closest(
        SELECTORS.paginationLink
      );

      if (
        paginationLink &&
        this.contains(paginationLink) &&
        paginationLink.href
      ) {
        event.preventDefault();

        this.loadUrl(paginationLink.href, {
          updateHistory: true,
          scrollToCatalog: true
        });

        return;
      }

      const addButton = event.target.closest(
        SELECTORS.addButton
      );

      if (addButton && this.contains(addButton)) {
        event.preventDefault();
        this.addToCart(addButton);
      }
    }

    handleChange(event) {
      const variantSelect = event.target.closest(
        SELECTORS.variantSelect
      );

      if (
        variantSelect &&
        this.contains(variantSelect)
      ) {
        this.updateVariant(variantSelect);
        return;
      }

      const filterInput = event.target.closest(
        SELECTORS.filterInput
      );

      if (
        filterInput &&
        this.contains(filterInput)
      ) {
        this.updateFilterLabels();

        const form = filterInput.closest(
          SELECTORS.facetsForm
        );

        if (
          form &&
          form.dataset.autoSubmit === 'true'
        ) {
          if (
            filterInput.matches(
              SELECTORS.priceInput
            )
          ) {
            this.schedulePriceSubmit(form);
          } else {
            this.scheduleFilterSubmit(form);
          }
        }
      }
    }

    handleSubmit(event) {
      const form = event.target.closest(
        SELECTORS.facetsForm
      );

      if (!form || !this.contains(form)) return;

      event.preventDefault();

      const url = this.buildFormUrl(form);

      this.closeAllPanels();

      this.loadUrl(url, {
        updateHistory: true,
        scrollToCatalog: false
      });
    }

    scheduleFilterSubmit(form) {
      window.clearTimeout(this.filterTimer);

      this.filterTimer = window.setTimeout(() => {
        const url = this.buildFormUrl(form);

        this.loadUrl(url, {
          updateHistory: true,
          scrollToCatalog: false
        });
      }, 180);
    }

    schedulePriceSubmit(form) {
      window.clearTimeout(this.priceTimer);

      this.priceTimer = window.setTimeout(() => {
        const url = this.buildFormUrl(form);

        this.loadUrl(url, {
          updateHistory: true,
          scrollToCatalog: false
        });
      }, 650);
    }

    buildFormUrl(form) {
      const formData = new FormData(form);
      const searchParams = new URLSearchParams();

      formData.forEach((value, key) => {
        const normalizedValue = String(value).trim();

        if (normalizedValue !== '') {
          searchParams.append(key, normalizedValue);
        }
      });

      const baseUrl =
        this.dataset.collectionUrl ||
        window.location.pathname;

      const query = searchParams.toString();

      return query
        ? `${baseUrl}?${query}`
        : baseUrl;
    }

    openDrawer(trigger) {
      const drawer = this.querySelector(
        SELECTORS.drawer
      );

      if (!drawer) return;

      this.closeSortSheet(false);

      this.lastFocusedElement = trigger;

      drawer.classList.add(CLASSES.open);
      drawer.setAttribute('aria-hidden', 'false');

      trigger?.setAttribute('aria-expanded', 'true');

      this.lockBody();

      window.requestAnimationFrame(() => {
        const closeButton = drawer.querySelector(
          SELECTORS.filterClose
        );

        closeButton?.focus({
          preventScroll: true
        });
      });
    }

    closeDrawer(restoreFocus = true) {
      const drawer = this.querySelector(
        SELECTORS.drawer
      );

      if (!drawer) return;

      drawer.classList.remove(CLASSES.open);
      drawer.setAttribute('aria-hidden', 'true');

      this.querySelectorAll(
        SELECTORS.filterOpen
      ).forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      this.unlockBodyIfPossible();

      if (
        restoreFocus &&
        this.lastFocusedElement instanceof HTMLElement
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      }
    }

    openSortSheet(trigger) {
      const sortSheet = this.querySelector(
        SELECTORS.sortSheet
      );

      if (!sortSheet) return;

      this.closeDrawer(false);

      this.lastFocusedElement = trigger;

      sortSheet.classList.add(CLASSES.open);
      sortSheet.setAttribute('aria-hidden', 'false');

      trigger?.setAttribute('aria-expanded', 'true');

      this.lockBody();

      window.requestAnimationFrame(() => {
        const closeButton = sortSheet.querySelector(
          SELECTORS.sortClose
        );

        closeButton?.focus({
          preventScroll: true
        });
      });
    }

    closeSortSheet(restoreFocus = true) {
      const sortSheet = this.querySelector(
        SELECTORS.sortSheet
      );

      if (!sortSheet) return;

      sortSheet.classList.remove(CLASSES.open);
      sortSheet.setAttribute('aria-hidden', 'true');

      this.querySelectorAll(
        SELECTORS.sortOpen
      ).forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      this.unlockBodyIfPossible();

      if (
        restoreFocus &&
        this.lastFocusedElement instanceof HTMLElement
      ) {
        this.lastFocusedElement.focus({
          preventScroll: true
        });
      }
    }

    closeAllPanels() {
      this.closeDrawer(false);
      this.closeSortSheet(false);
      this.lastFocusedElement = null;
      this.unlockBody();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      const drawer = this.querySelector(
        `${SELECTORS.drawer}.${CLASSES.open}`
      );

      if (drawer) {
        this.closeDrawer();
        return;
      }

      const sortSheet = this.querySelector(
        `${SELECTORS.sortSheet}.${CLASSES.open}`
      );

      if (sortSheet) {
        this.closeSortSheet();
      }
    }

    lockBody() {
      document.body.classList.add(
        CLASSES.bodyLock
      );
    }

    unlockBody() {
      document.body.classList.remove(
        CLASSES.bodyLock
      );
    }

    unlockBodyIfPossible() {
      const hasOpenPanel =
        this.querySelector(
          `${SELECTORS.drawer}.${CLASSES.open}`
        ) ||
        this.querySelector(
          `${SELECTORS.sortSheet}.${CLASSES.open}`
        );

      if (!hasOpenPanel) {
        this.unlockBody();
      }
    }

    applySort(sortOption) {
      const value = sortOption.dataset.value;

      if (!value) return;

      this.querySelectorAll(
        SELECTORS.sortOption
      ).forEach((option) => {
        const isActive = option === sortOption;

        option.classList.toggle(
          CLASSES.active,
          isActive
        );

        option.setAttribute(
          'aria-checked',
          String(isActive)
        );
      });

      const hiddenSortSelect = this.querySelector(
        SELECTORS.sortSelect
      );

      if (hiddenSortSelect) {
        hiddenSortSelect.value = value;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');

      this.closeSortSheet(false);

      this.loadUrl(url.toString(), {
        updateHistory: true,
        scrollToCatalog: false
      });
    }

    async loadUrl(
      url,
      {
        updateHistory = true,
        scrollToCatalog = false
      } = {}
    ) {
      if (!url || this.isRequesting) {
        this.cancelCurrentRequest();
      }

      this.cancelCurrentRequest();

      this.abortController = new AbortController();
      this.isRequesting = true;

      this.setLoading(true);

      try {
        const requestUrl = new URL(
          url,
          window.location.origin
        );

        requestUrl.searchParams.set(
          'section_id',
          this.dataset.sectionId
        );

        const response = await fetch(
          requestUrl.toString(),
          {
            method: 'GET',
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest'
            },
            signal: this.abortController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            'No pudimos actualizar el catálogo.'
          );
        }

        const html = await response.text();
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(
          html,
          'text/html'
        );

        const newComponent =
          parsedDocument.querySelector(
            `${SELECTORS.component}[data-section-id="${this.dataset.sectionId}"]`
          ) ||
          parsedDocument.querySelector(
            SELECTORS.component
          );

        const newRender =
          newComponent?.querySelector(
            SELECTORS.render
          );

        if (!newRender || !this.renderContainer) {
          throw new Error(
            'No se encontró el contenido actualizado.'
          );
        }

        this.renderContainer.innerHTML =
          newRender.innerHTML;

        this.cacheElements();
        this.initializeVariants();
        this.updateFilterLabels();

        if (updateHistory) {
          const cleanUrl = new URL(
            url,
            window.location.origin
          );

          cleanUrl.searchParams.delete(
            'section_id'
          );

          window.history.pushState(
            {},
            '',
            cleanUrl.toString()
          );
        }

        if (scrollToCatalog) {
          window.requestAnimationFrame(() => {
            this.scrollCatalogIntoView();
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error(
          '[ChemarryCollectionGrid]',
          error
        );

        this.showToast(
          error.message ||
            'No pudimos actualizar el catálogo.',
          true
        );
      } finally {
        this.isRequesting = false;
        this.abortController = null;
        this.setLoading(false);
      }
    }

    cancelCurrentRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.isRequesting = false;
    }

    setLoading(isLoading) {
      this.classList.toggle(
        CLASSES.loading,
        isLoading
      );

      this.setAttribute(
        'aria-busy',
        String(isLoading)
      );
    }

    handlePopState() {
      this.loadUrl(window.location.href, {
        updateHistory: false,
        scrollToCatalog: false
      });
    }

    updateFilterLabels() {
      this.querySelectorAll(
        SELECTORS.filterInput
      ).forEach((input) => {
        const label = input.closest(
          '.chemarry-collection-grid__facet-label'
        );

        if (!label) return;

        if (
          input.type === 'checkbox' ||
          input.type === 'radio'
        ) {
          label.classList.toggle(
            CLASSES.active,
            input.checked
          );
        }
      });

      this.updateTemporaryFilterCount();
    }

    updateTemporaryFilterCount() {
      const mobileForm = this.querySelector(
        `${SELECTORS.facetsForm}[data-grid-form-role="mobile"]`
      );

      if (!mobileForm) return;

      let activeCount = 0;

      mobileForm.querySelectorAll(
        'input[type="checkbox"]:checked'
      ).forEach(() => {
        activeCount += 1;
      });

      mobileForm.querySelectorAll(
        SELECTORS.priceInput
      ).forEach((input) => {
        if (String(input.value).trim() !== '') {
          activeCount += 1;
        }
      });

      this.querySelectorAll(
        SELECTORS.filterCount
      ).forEach((element) => {
        element.textContent = activeCount;
        element.hidden = activeCount === 0;
      });
    }

    initializeVariants() {
      this.querySelectorAll(
        SELECTORS.variantSelect
      ).forEach((select) => {
        this.updateVariant(select);
      });
    }

    updateVariant(select) {
      const selectedOption =
        select.options[select.selectedIndex];

      if (!selectedOption) return;

      const card = select.closest(
        '.chemarry-collection-card'
      );

      if (!card) return;

      const button = card.querySelector(
        SELECTORS.addButton
      );

      const image = card.querySelector(
        SELECTORS.productImage
      );

      const price = card.querySelector(
        SELECTORS.price
      );

      const comparePrice = card.querySelector(
        SELECTORS.comparePrice
      );

      const availability = card.querySelector(
        SELECTORS.availability
      );

      const variantId =
        selectedOption.value;

      const isAvailable =
        selectedOption.dataset.available === 'true';

      const variantPrice =
        selectedOption.dataset.price || '';

      const variantComparePrice =
        selectedOption.dataset.comparePrice || '';

      const imageUrl =
        selectedOption.dataset.image || '';

      const imageAlt =
        selectedOption.dataset.imageAlt || '';

      if (button) {
        button.dataset.variantId = variantId;
        button.dataset.available = String(
          isAvailable
        );

        button.disabled = !isAvailable;

        button.classList.toggle(
          CLASSES.soldOut,
          !isAvailable
        );

        const availableText =
          button.dataset.availableText ||
          button.dataset.buttonText ||
          'Agregar';

        const soldOutText =
          button.dataset.soldOutText ||
          'Agotado';

        button.textContent = isAvailable
          ? availableText
          : soldOutText;
      }

      if (price && variantPrice) {
        price.textContent = variantPrice;

        price.classList.toggle(
          'is-offer',
          Boolean(variantComparePrice)
        );
      }

      if (comparePrice) {
        if (variantComparePrice) {
          comparePrice.textContent =
            variantComparePrice;

          comparePrice.hidden = false;
        } else {
          comparePrice.textContent = '';
          comparePrice.hidden = true;
        }
      }

      if (availability) {
        const availableText =
          availability.dataset.availableText ||
          'Disponible';

        const soldOutText =
          availability.dataset.soldOutText ||
          'Agotado';

        availability.textContent = isAvailable
          ? availableText
          : soldOutText;

        availability.classList.toggle(
          CLASSES.soldOut,
          !isAvailable
        );
      }

      if (image && imageUrl) {
        image.src = imageUrl;
        image.removeAttribute('srcset');

        if (imageAlt) {
          image.alt = imageAlt;
        }
      }
    }

    async addToCart(button) {
      const variantId = Number(
        button.dataset.variantId
      );

      if (
        !variantId ||
        button.disabled ||
        button.classList.contains(CLASSES.loading)
      ) {
        return;
      }

      this.setAddButtonLoading(button, true);

      try {
        const sectionIds =
          this.getCartSectionIds();

        const requestBody = {
          items: [
            {
              id: variantId,
              quantity: 1
            }
          ]
        };

        if (sectionIds.length > 0) {
          requestBody.sections =
            sectionIds.join(',');

          requestBody.sections_url =
            window.location.pathname +
            window.location.search;
        }

        const root =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(requestBody)
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              'No pudimos agregar el producto.'
          );
        }

        await this.refreshCart(result);

        this.showToast(
          button.dataset.successMessage ||
            'Producto agregado al carrito.',
          false
        );
      } catch (error) {
        console.error(
          '[ChemarryCollectionGrid cart]',
          error
        );

        this.showToast(
          error.message ||
            'No pudimos agregar el producto.',
          true
        );
      } finally {
        this.setAddButtonLoading(
          button,
          false
        );
      }
    }

    setAddButtonLoading(button, isLoading) {
      button.classList.toggle(
        CLASSES.loading,
        isLoading
      );

      button.setAttribute(
        'aria-busy',
        String(isLoading)
      );

      if (isLoading) {
        button.disabled = true;
        return;
      }

      button.disabled =
        button.dataset.available !== 'true';

      button.classList.toggle(
        CLASSES.soldOut,
        button.disabled
      );

      const availableText =
        button.dataset.availableText ||
        button.dataset.buttonText ||
        'Agregar';

      const soldOutText =
        button.dataset.soldOutText ||
        'Agotado';

      button.textContent = button.disabled
        ? soldOutText
        : availableText;
    }

    getCartSectionIds() {
      const sectionIds = new Set();

      const cartDrawer =
        document.querySelector('cart-drawer');

      const cartNotification =
        document.querySelector(
          'cart-notification'
        );

      [cartDrawer, cartNotification].forEach(
        (component) => {
          if (
            component &&
            typeof component.getSectionsToRender ===
              'function'
          ) {
            component
              .getSectionsToRender()
              .forEach((section) => {
                if (section?.id) {
                  sectionIds.add(section.id);
                }
              });
          }
        }
      );

      sectionIds.add('cart-icon-bubble');

      return Array.from(sectionIds);
    }

    async refreshCart(result) {
      const cartDrawer =
        document.querySelector('cart-drawer');

      const cartNotification =
        document.querySelector(
          'cart-notification'
        );

      if (
        result.sections &&
        cartDrawer &&
        typeof cartDrawer.renderContents ===
          'function'
      ) {
        cartDrawer.renderContents(result);

        window.setTimeout(() => {
          this.openCartDrawer(cartDrawer);
        }, 80);

        return;
      }

      if (
        result.sections &&
        cartNotification &&
        typeof cartNotification.renderContents ===
          'function'
      ) {
        cartNotification.renderContents(result);

        if (
          typeof cartNotification.open ===
          'function'
        ) {
          cartNotification.open();
        }

        return;
      }

      await this.refreshCartManually();
    }

    async refreshCartManually() {
      const root =
        window.Shopify?.routes?.root || '/';

      const sectionIds =
        this.getCartSectionIds();

      try {
        const response = await fetch(
          `${root}?sections=${encodeURIComponent(
            sectionIds.join(',')
          )}`,
          {
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error();
        }

        const sections = await response.json();

        this.replaceCartSection(
          sections,
          'cart-icon-bubble',
          '#shopify-section-cart-icon-bubble'
        );

        this.replaceCartSection(
          sections,
          'cart-drawer',
          '#shopify-section-cart-drawer'
        );

        const cartDrawer =
          document.querySelector(
            'cart-drawer'
          );

        if (cartDrawer) {
          window.setTimeout(() => {
            this.openCartDrawer(cartDrawer);
          }, 80);
        }
      } catch (error) {
        window.location.href = `${root}cart`;
      }
    }

    replaceCartSection(
      sections,
      sectionId,
      selector
    ) {
      const html = sections?.[sectionId];

      if (!html) return;

      const parser = new DOMParser();

      const parsedDocument =
        parser.parseFromString(
          html,
          'text/html'
        );

      const newSection =
        parsedDocument.querySelector(selector) ||
        parsedDocument.body.firstElementChild;

      const currentSection =
        document.querySelector(selector);

      if (newSection && currentSection) {
        currentSection.replaceWith(newSection);
      }
    }

    openCartDrawer(cartDrawer) {
      if (!cartDrawer) return;

      if (
        typeof cartDrawer.open === 'function'
      ) {
        cartDrawer.open();
        return;
      }

      cartDrawer.classList.add('active');
      document.body.classList.add(
        'overflow-hidden'
      );

      const innerDrawer =
        cartDrawer.querySelector(
          '.drawer__inner'
        );

      if (innerDrawer) {
        innerDrawer.setAttribute(
          'tabindex',
          '-1'
        );

        innerDrawer.focus({
          preventScroll: true
        });
      }
    }

    showToast(message, isError = false) {
      this.toast =
        this.querySelector(SELECTORS.toast) ||
        this.toast;

      if (!this.toast || !message) return;

      window.clearTimeout(this.toastTimer);

      this.toast.textContent = message;

      this.toast.classList.toggle(
        CLASSES.error,
        isError
      );

      this.toast.classList.add(
        CLASSES.visible
      );

      this.toast.setAttribute(
        'role',
        isError ? 'alert' : 'status'
      );

      this.toastTimer = window.setTimeout(() => {
        this.toast?.classList.remove(
          CLASSES.visible
        );
      }, 3200);
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }
  }

  if (
    !customElements.get(
      'chemarry-collection-grid'
    )
  ) {
    customElements.define(
      'chemarry-collection-grid',
      ChemarryCollectionGrid
    );
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const components =
        event.target.querySelectorAll(
          SELECTORS.component
        );

      components.forEach((component) => {
        if (
          component instanceof
            ChemarryCollectionGrid &&
          !component.isInitialized
        ) {
          component.connectedCallback();
        }
      });
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const components =
        event.target.querySelectorAll(
          SELECTORS.component
        );

      components.forEach((component) => {
        if (
          component instanceof
          ChemarryCollectionGrid
        ) {
          component.disconnectedCallback();
        }
      });
    }
  );
})();